/**
 * Future Closure Results Component
 * Displays pro forma projections and total demand
 */

import React, { useState } from 'react';
import { MonteCarloParams, SensitivityParams, downloadFutureClosurePDF } from '../services/futureClosureApi';

interface FutureClosureResultsProps {
  result: any;
  reportId: string;
  calculationId?: string;
  onRunMonteCarlo: (params: MonteCarloParams) => void;
  onRunSensitivity: (params: SensitivityParams) => void;
  monteCarloLoading: boolean;
  sensitivityLoading: boolean;
  onRecalculate?: () => void;  // P3: Recalculate button callback
}

export default function FutureClosureResults({
  result,
  reportId,
  calculationId,
  onRunMonteCarlo,
  onRunSensitivity,
  monteCarloLoading,
  sensitivityLoading,
  onRecalculate
}: FutureClosureResultsProps) {
  const [showMonthlyDetails, setShowMonthlyDetails] = useState(false);
  const [showDefensibility, setShowDefensibility] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const handleDownloadPDF = async () => {
    setPdfLoading(true);
    setPdfError(null);
    try {
      await downloadFutureClosurePDF(reportId, calculationId);
    } catch (err: any) {
      console.error('Error downloading PDF:', err);
      setPdfError(err.response?.data?.detail || err.message || 'Failed to download PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  if (!result) return null;

  const { parameters, pro_forma_summary, one_time_costs, total_demand, pro_forma_monthly, continuation_percentages, data_source } = result;

  return (
    <div className="space-y-6">
      {/* P&L Data Source Indicator */}
      {continuation_percentages && (
        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-blue-900 flex items-center">
              <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Expense Continuation Rates
            </h4>
            {data_source && (
              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                {typeof data_source === 'string' ? data_source : 'Granular P&L Data'}
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 text-sm">
            {Object.entries(continuation_percentages).map(([category, rate]) => (
              <div key={category} className="bg-white rounded-lg p-3 border border-blue-100">
                <div className="text-xs text-gray-500 mb-1 capitalize">
                  {category.replace(/_/g, ' ')}
                </div>
                <div className="text-lg font-bold text-blue-700">
                  {typeof rate === 'number' ? `${rate.toFixed(0)}%` : rate}
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-3">
            These rates determine what percentage of each expense continues during closure. Rates are calculated based on your uploaded P&L data and industry standards.
          </p>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Lost Profits */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white shadow-lg">
          <p className="text-blue-100 text-sm font-medium mb-1">Lost Profits (36 mo)</p>
          <p className="text-3xl font-bold">
            ${pro_forma_summary?.total_lost_profits?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0'}
          </p>
        </div>

        {/* One-Time Costs */}
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg p-6 text-white shadow-lg">
          <p className="text-orange-100 text-sm font-medium mb-1">One-Time Costs</p>
          <p className="text-3xl font-bold">
            ${one_time_costs?.total?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0'}
          </p>
          <p className="text-orange-100 text-xs mt-2">
            Shutdown: ${one_time_costs?.shutdown?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} | 
            Restart: ${one_time_costs?.restart?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        </div>

        {/* Total Demand */}
        <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg p-6 text-white shadow-lg">
          <p className="text-blue-100 text-sm font-medium mb-1">Total Demand</p>
          <p className="text-3xl font-bold">
            ${total_demand?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0'}
          </p>
        </div>
      </div>

      {/* Parameters Used */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-3">Calculation Parameters</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Aggression:</span>
            <span className="ml-2 font-semibold text-blue-600">{parameters?.aggression}/100</span>
          </div>
          <div>
            <span className="text-gray-600">Growth Rate:</span>
            <span className="ml-2 font-semibold">{parameters?.growth_rate}%</span>
          </div>
          <div>
            <span className="text-gray-600">Base Period:</span>
            <span className="ml-2 font-semibold">{result.base_period_summary?.period_type} {result.base_period_summary?.months_analyzed} mo</span>
          </div>
          <div>
            <span className="text-gray-600">Closure:</span>
            <span className="ml-2 font-semibold">{parameters?.closure_duration_months} months</span>
          </div>
        </div>
      </div>

      {/* Monthly Pro Forma Toggle */}
      <button
        onClick={() => setShowMonthlyDetails(!showMonthlyDetails)}
        className="w-full bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg p-4 flex items-center justify-between transition-colors"
      >
        <span className="font-medium text-gray-900">36-Month Pro Forma Details</span>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform ${showMonthlyDetails ? 'transform rotate-180' : ''}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>

      {/* Monthly Pro Forma Table */}
      {showMonthlyDetails && pro_forma_monthly && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Revenue</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Expenses</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Lost Profit</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pro_forma_monthly.map((month: any, idx: number) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {month.month_number}. {month.month_name}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-900">
                      ${month.projected_revenue?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-900">
                      ${month.continued_expenses?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-semibold text-blue-600">
                      ${month.lost_profit?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Defensibility Notes Toggle */}
      <button
        onClick={() => setShowDefensibility(!showDefensibility)}
        className="w-full bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg p-4 flex items-center justify-between transition-colors"
      >
        <span className="font-medium text-green-900">Defensibility Notes</span>
        <svg
          className={`w-5 h-5 text-green-400 transition-transform ${showDefensibility ? 'transform rotate-180' : ''}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>

      {showDefensibility && result.defensibility_notes && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <ul className="space-y-2">
            {result.defensibility_notes.map((note: string, idx: number) => (
              <li key={idx} className="flex items-start space-x-2 text-sm text-green-900">
                <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>{note}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Action Buttons */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Sensitivity Analysis Button */}
        <button
          onClick={() => onRunSensitivity({ 
            aggression: parameters?.aggression || 50,
            use_best_base: result.parameters?.use_best_base,
            base_months: result.parameters?.base_months
          })}
          disabled={sensitivityLoading}
          className={`py-4 rounded-lg font-semibold text-white transition-all ${
            sensitivityLoading
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 shadow-lg hover:shadow-xl'
          }`}
        >
          {sensitivityLoading ? (
            <span className="flex items-center justify-center space-x-2">
              <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Analyzing...</span>
            </span>
          ) : (
            <span className="flex items-center justify-center space-x-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
              </svg>
              <span>Run Sensitivity Analysis (Tornado Chart)</span>
            </span>
          )}
        </button>

        {/* Monte Carlo Button */}
        <button
          onClick={() => onRunMonteCarlo({ aggression: parameters?.aggression || 50 })}
          disabled={monteCarloLoading}
          className={`py-4 rounded-lg font-semibold text-white transition-all ${
            monteCarloLoading
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 shadow-lg hover:shadow-xl'
          }`}
        >
        {monteCarloLoading ? (
          <span className="flex items-center justify-center space-x-2">
            <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>Running 50,000 Simulations...</span>
          </span>
        ) : (
          <span className="flex items-center justify-center space-x-2">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
            </svg>
            <span>Run Monte Carlo Simulation (P10/P50/P90)</span>
          </span>
        )}
      </button>
      </div>

      {/* Action Buttons Section */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-sm text-gray-600">
            <p className="font-medium text-gray-900">Analysis Actions</p>
            <p>Recalculate with different parameters or export to PDF.</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Recalculate Button */}
            {onRecalculate && (
              <button
                onClick={onRecalculate}
                className="flex items-center space-x-2 px-5 py-3 rounded-lg font-semibold text-blue-600 border-2 border-blue-600 hover:bg-blue-50 transition-all"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>Recalculate</span>
              </button>
            )}
            {/* Download PDF Button */}
            <button
              onClick={handleDownloadPDF}
              disabled={pdfLoading}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold text-white transition-all ${
                pdfLoading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 shadow-lg hover:shadow-xl'
              }`}
            >
            {pdfLoading ? (
              <>
                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Generating PDF...</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                <span>Download PDF Report</span>
              </>
            )}
            </button>
          </div>
        </div>
        {pdfError && (
          <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
            {pdfError}
          </div>
        )}
      </div>
    </div>
  );
}
